import React, { useEffect, useState } from "react";
import { FaArrowLeft, FaTimes } from "react-icons/fa";
import { LiaRupeeSignSolid } from "react-icons/lia";
import { useNavigate, useParams } from "react-router-dom";
import Toast from "../../components/Toast/Toast";
import { getOrderByIdAPI, updateOrderStatusAPI } from "../../utils/api";
import { BASE_URL } from '../../utils/configConstants';
import "./OrderDetails.css";

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [updateLoading, setUpdateLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, type: '', message: '' });

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, type: '', message: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await getOrderByIdAPI(orderId);
      if (response?.status === 200) {
        console.log(response?.data)
        setOrder(response?.data);
      } else {
        setError(response?.message || "Failed to fetch order details");
      }
    } catch (err) {
      console.error("Error fetching order details:", err);
      setError("Failed to fetch order details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orderId) {
      fetchOrderDetails();
    }
  }, [orderId]);

  const handleUpdateStatus = () => {
    setNewStatus(order?.order_status || "");
    setShowUpdateModal(true);
  };

  const handleStatusUpdate = async () => {
    if (!newStatus || newStatus === order?.order_status) {
      setShowUpdateModal(false);
      return;
    }

    try {
      setUpdateLoading(true);
      const response = await updateOrderStatusAPI({
        order_id: order._id,
        order_status: newStatus
      });

      if (response?.status === 200) {
        setToast({ show: true, type: 'success', message: 'Order status updated successfully' });
        setShowUpdateModal(false);
        fetchOrderDetails(); // Refresh order data
      } else {
        setToast({ show: true, type: 'error', message: response?.message || 'Failed to update order status' });
      }
    } catch (err) {
      console.error("Error updating order status:", err);
      setToast({ show: true, type: 'error', message: 'Failed to update order status' });
    } finally {
      setUpdateLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";
    if (imagePath.startsWith('http')) return imagePath;
    return `${BASE_URL}/${imagePath}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const orderedDate = formatDate(order?.order_date);

  const statusTimeline = [
    { label: 'Ordered', status: 'ordered' },
    { label: 'Processing', status: 'processing' },
    { label: 'Delivered', status: 'delivered' }
  ];

  const subtotal = order?.products?.reduce(
    (sum, p) => sum + p?.price * p?.qty,
    0
  );
  const taxAmount = (subtotal * order?.tax_rate) / 100;

  // Calculate discount based on coupon type
  const getDiscountAmount = () => {
    if (!order?.coupon_id) return 0;
    
    if (order?.discount_type === 'percentage' && order?.discount_percentage) {
      return (subtotal * parseFloat(order.discount_percentage)) / 100;
    } else if (order?.discount_type === 'flat' && order?.discount_amount) {
      return parseFloat(order.discount_amount);
    }
    return 0;
  };

  const discountAmount = getDiscountAmount();

  const getCurrentStatusIndex = () => {
    const currentStatus = order?.order_status?.toLowerCase();
    return statusTimeline.findIndex(step => step.status === currentStatus);
  };

  const currentStatusIdx = getCurrentStatusIndex();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading order details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
        <button onClick={() => navigate('/order')} className="back-button">
          Back to Orders
        </button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="empty-state">
        <h3>Order Not Found</h3>
        <p>The order you're looking for doesn't exist.</p>
        <button onClick={() => navigate('/order')} className="back-button">
          Back to Orders
        </button>
      </div>
    );
  }

  return (
    <div className="order-details-container">
      <div className="order-details-header">
        <button className="back-button" onClick={() => navigate('/order')}>
          <FaArrowLeft /> Back to Orders
        </button>
      </div>

      <div className="order-details-card">
        <div className="order-summary">
          <div className="order-summary-header">
            <div className="order-id">
              <span className="order-id-value">Order ID: {order._id}</span>
              <span className="order-date">Ordered on {orderedDate}</span>
            </div>
            <div className="order-status">
              <span
                className="status-badge"
                style={{
                  backgroundColor:
                    order?.order_status?.toLowerCase() === 'processing' ? '#FF980033' :
                      order?.order_status?.toLowerCase() === 'delivered' ? '#17BF5233' :
                        order?.order_status?.toLowerCase() === 'cancelled' ? '#F2050533' :
                          order?.order_status?.toLowerCase() === 'ordered' ? '#0A727E33' : '#0A727E33',
                  color:
                    order?.order_status?.toLowerCase() === 'processing' ? '#FF9800' :
                      order?.order_status?.toLowerCase() === 'delivered' ? '#17BF52' :
                        order?.order_status?.toLowerCase() === 'cancelled' ? '#F24405' :
                          order?.order_status?.toLowerCase() === 'ordered' ? '#0A727E' : '#0A727E'
                }}
              >
                {order?.order_status?.charAt(0).toUpperCase() + order.order_status?.slice(1)}
              </span>
            </div>
          </div>

          {order?.order_status?.toLowerCase() !== 'cancelled' && (
            <div className="status-timeline">
              {statusTimeline.map((step, idx) => {
                const isActive = idx <= currentStatusIdx;
                return (
                  <React.Fragment key={step.label}>
                    <div className="timeline-step">
                      <div className={`step-indicator ${isActive ? 'active' : ''}`}>
                        <div className="step-dot"></div>
                      </div>
                      <div className={`step-label ${isActive ? 'active' : ''}`}>
                        {step.label}
                      </div>
                    </div>
                    {idx < statusTimeline.length - 1 && (
                      <div className={`timeline-line ${isActive ? 'active' : ''}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        <div className="order-section">
          <h2 className="section-title">Products</h2>

          <div className="order-details-products-list">
            {order.products.map((product, index) => (
              <div key={product?._id || index} className="order-details-product-item">
                <div className="order-details-product-main">
                  <img
                    src={getImageUrl(product?.image)}
                    alt={product?.name}
                    className="order-details-product-image"
                  />
                  <div className="order-details-product-details">
                    <h3 className="order-details-product-name">{product?.name}</h3>
                    <div className="order-details-product-meta">
                      {product?.sku && <span className="meta-item">SKU: {product?.sku}</span>}
                      {product?.size && <span className="meta-item">Size: {product?.size}</span>}
                      {product?.qty && <span className="meta-item">Qty: {product?.qty}</span>}
                      {product?.color && <span className="meta-item">Color: {product?.color}</span>}
                      {product?.brand && <span className="meta-item">Brand: {product?.brand}</span>}
                      {product?.fabric && <span className="meta-item">Fabric: {product?.fabric}</span>}
                    </div>
                    <div className="product-price-row">
                      <div className="product-price">
                        <LiaRupeeSignSolid className="rupee-icon-details" />
                        {product?.price}
                      </div>
                      {product?.qty > 1 && (
                        <div className="product-total">
                          <LiaRupeeSignSolid className="rupee-icon-total" />
                          {product?.price * product?.qty}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {product?.rating_review && (
                  <div className="product-rating">
                    <div className="rating-header">Rating & Review</div>
                    <div className="rating-stars">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span
                          key={star}
                          className={`star ${star <= product.rating_review.rating ? 'filled' : ''}`}
                        >
                          ★
                        </span>
                      ))}
                    </div>
                    <div className="rating-review">{product.rating_review.review}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="order-section">
          
          <div className="total-row">
            <span>Subtotal</span>
            <span>
              <LiaRupeeSignSolid className="rupee-icon-subtotal" />
              {subtotal?.toFixed(2)}
            </span>
          </div>

          {order?.coupon_id && discountAmount > 0 && (
            <div className="total-row">
              <span>
                Discount {order?.discount_type === 'percentage' && `(${order?.discount_percentage}%)`}
              </span>
              <span>
                -<LiaRupeeSignSolid className="rupee-icon-subtotal" />
                {discountAmount?.toFixed(2)}
              </span>
            </div>
          )}

          {order?.tax_rate > 0 && (
            <div className="total-row">
              <span>Tax ({order?.tax_rate}%)</span>
              <span>
                <LiaRupeeSignSolid className="rupee-icon-subtotal" />
                {taxAmount?.toFixed(2)}
              </span>
            </div>
          )}

          <div className={`total-row total-final ${order?.tax_rate > 0 || (order?.coupon_id && discountAmount > 0) ? 'with-border-top' : ''}`}>
            <span>Total</span>
            <span>
              <LiaRupeeSignSolid className="rupee-icon-totals" />
              {order.total}
            </span>
          </div>
        </div>

        <div className="order-section">
          <h2 className="section-title">Shipping Address</h2>
          <div className="shipping-address">
            <div className="customer-name">{order?.name}</div>
            <div className="address-line">Phone: {order?.country_code} {order?.contact}</div>
            <div className="address-line">{order?.address}</div>
            <div className="address-line">
              {order?.city}, {order?.state} {order?.pincode}
            </div>
          </div>
        </div>
      </div>

      <Toast toast={toast} />
    </div>
  );
};

export default OrderDetails; 